import { AxiosError } from 'axios';
import { queryClient } from '~/main';
import { showMessage } from '~/actions';
import { store } from '~/store/configureStore';

export const resetInviteProfessionals = () => {
  queryClient.resetQueries({ queryKey: ['favorite-professionals'] });
  queryClient.resetQueries({ queryKey: ['recent-professionals'] });
  queryClient.resetQueries({ queryKey: ['recommended-professionals'] });
};

export interface IShowErrorMessageOptions {
  error: unknown;
  title?: string;
  defaultMessage?: string;
  onRightButtonClick?(): void;
}

/**
 * Extracts error message from various error types and displays it using Redux showMessage action
 *
 * @example
 * // Basic usage
 * showErrorMessage({ error });
 *
 * // With custom title
 * showErrorMessage({ error, title: 'Failed to Load Data' });
 *
 * // With default fallback message
 * showErrorMessage({ error, title: 'Upload Failed', defaultMessage: 'Could not upload file' });
 */
export const showErrorMessage = (options: IShowErrorMessageOptions): void => {
  const { error, title = 'Error', defaultMessage = 'An unexpected error occurred' } = options;

  let message: string;

  if (typeof error === 'string') {
    // Error is a string, use it directly
    message = error;
  } else if (error && typeof error === 'object' && 'isAxiosError' in error) {
    // Axios error - extract from response data
    const axiosError = error as AxiosError<any>;

    // Try to extract from error or errors array first
    const apiErrorMessage =
      axiosError.response?.data?.error || axiosError.response?.data?.errors?.[0]?.message;

    if (apiErrorMessage) {
      message = apiErrorMessage;
    } else if (axiosError.response?.data?.message) {
      message = axiosError.response.data.message;
    } else if (axiosError.message) {
      message = axiosError.message;
    } else {
      message = defaultMessage;
    }
  } else if (error && typeof error === 'object' && 'message' in error) {
    // Generic Error object
    message = (error as Error).message || defaultMessage;
  } else {
    // Unknown error type
    message = defaultMessage;
  }

  store.dispatch(
    showMessage({
      title,
      message,
      isError: true,
      ...(options.onRightButtonClick
        ? {
            popupProps: { rightButtonAction: options.onRightButtonClick },
          }
        : {}),
    }),
  );
};
